import { useCallback } from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  CompensationFilterOptionsDocument,
  CompensationFilterOptionsQuery,
  CompensationsListFragment,
  CycleCurrencyConfigurationFragment,
} from 'generated/resources/graphql'

export function useCompensationsListFilterOptions(companyId: number) {
  const [fetchFilters, { data, loading, error }] = useLazyQuery<CompensationFilterOptionsQuery>(
    CompensationFilterOptionsDocument,
    {
      variables: { companyId },
    }
  )

  const getFilterData = useCallback(async () => {
    if (data) {
      return data
    }
    const result = await fetchFilters()
    return result.data
  }, [data, fetchFilters])

  const loadLegalEntityOptions = useCallback(
    async (
      payrollRun: CompensationsListFragment & {
        cycle?: CycleCurrencyConfigurationFragment | null
      }
    ) => {
      const filterData = await getFilterData()
      const legalEntities = filterData?.apiCore?.company?.legalEntities || []
      const filteredEntities = payrollRun?.cycle?.currency
        ? legalEntities.filter(
            (legalEntity) =>
              legalEntity.currency === payrollRun.cycle?.currency &&
              legalEntity.country === payrollRun.cycle?.country
          )
        : legalEntities
      return filteredEntities.map((legalEntity) => ({
        label: legalEntity.legalName || '',
        value: Number(legalEntity.id),
      }))
    },
    [getFilterData]
  )

  const loadTeamOptions = useCallback(async () => {
    const filterData = await getFilterData()
    return (
      filterData?.teams?.teamsConnection?.edges?.map((edge) => ({
        label: edge.node.name || '',
        value: Number(edge.node.id),
      })) || []
    )
  }, [getFilterData])

  const loadWorkplaceOptions = useCallback(async () => {
    const filterData = await getFilterData()
    return (
      filterData?.locations?.locationsConnection?.nodes?.map((location) => ({
        label: location.name || '',
        value: Number(location.id),
      })) || []
    )
  }, [getFilterData])

  return {
    loadLegalEntityOptions,
    loadTeamOptions,
    loadWorkplaceOptions,
    loading,
    error,
  }
}
